import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCustomerType } from '../context/CustomerTypeContext';
import './Cart.css';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, getTotalVAT, getCartPricing, clearCart } = useCart();
  const { isCorporate } = useCustomerType();
  const [showCheckoutValidation, setShowCheckoutValidation] = useState(false);
  const pricing = getCartPricing();
  const pricedItemsMap = new Map(pricing.pricedItems.map((item) => [item.id, item]));

  useEffect(() => {
    if (isCorporate) {
      navigate('/products');
    }
  }, [isCorporate, navigate]);

  if (isCorporate) {
    return null; // Will redirect
  }

  const getAvailableQuantity = (item) => {
    const stockQty = Math.max(0, parseInt(item.stock_quantity, 10) || 0);
    const defectiveQty = Math.max(0, parseInt(item.defective_count, 10) || 0);

    console.log(`Calculating available quantity for ${item.name}: stock=${stockQty}, defective=${defectiveQty}`);

    return Math.max(0, stockQty - defectiveQty);
  };

  const unavailableItems = cartItems.filter((item) => {
    const orderedQty = Math.max(0, parseInt(item.quantity, 10) || 0);
    const availableQty = getAvailableQuantity(item);
    return orderedQty > availableQty;
  });
  const hasUnavailableItems = unavailableItems.length > 0;

  // Prevent arrow keys from changing number input values
  const preventArrowKeyChange = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-container">
        <section className="cart-hero">
          <div className="container">
            <h1>Shopping Cart</h1>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="empty-cart">
              <p>Your cart is empty</p>
              <Link to="/products" className="btn btn-primary">
                Continue Shopping
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <section className="cart-hero">
        <div className="container">
          <h1>Shopping Cart</h1>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="cart-content">
            <div className="cart-items">
              <h2>Items in Cart ({cartItems.length})</h2>
              {showCheckoutValidation && hasUnavailableItems && (
                <div className="cart-checkout-error" role="alert" style={{ marginBottom: '16px' }}>
                  {unavailableItems.map((item) => {
                    const availableQty = getAvailableQuantity(item);
                    return (
                      <p key={item.id}>
                        {item.name}: ordered quantity ({item.quantity}) exceeds available quantity ({availableQty}).
                      </p>
                    );
                  })}
                </div>
              )}
              <div className="items-list">
                {cartItems.flatMap((item) => {
                    const priced = pricedItemsMap.get(item.id);
                    const hasSplit = priced && priced.bundled_quantity > 0 && priced.unbundled_quantity > 0;

                    if (hasSplit) {
                      const bundledUnitPrice = priced.bundled_line_total / priced.bundled_quantity;
                      const unbundledUnitPrice = priced.unbundled_unit_price;

                      return [
                        // Row 1 — bundled units
                        <div key={`${item.id}-bundled`} className="cart-item cart-item-split">
                          <div className="cart-item-details">
                            <h3>
                              {item.name}
                              <span className="bundle-split-tag" style={{ marginLeft: '8px' }}>BUNDLE OFFER</span>
                            </h3>
                            <p className="cart-item-price">kr {bundledUnitPrice.toFixed(2)} <span className="original-price">(was kr {priced.unbundled_unit_price.toFixed(2)})</span></p>
                            {item.weight && item.weight > 0 && (
                              <p className="cart-item-weight" style={{ fontSize: '0.85em', color: '#666' }}>📦 {item.weight}g</p>
                            )}
                          </div>
                          <div className="cart-item-quantity">
                            <label>Quantity:</label>
                            <div className="quantity-input quantity-input-readonly">
                              <span>{priced.bundled_quantity}</span>
                            </div>
                          </div>
                          <div className="cart-item-subtotal">
                            <p>Subtotal: kr {priced.bundled_line_total.toFixed(2)}</p>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="btn-remove"
                          >
                            Remove
                          </button>
                        </div>,

                        // Row 2 — unbundled units
                        <div key={`${item.id}-unbundled`} className="cart-item cart-item-split">
                          <div className="cart-item-details">
                            <h3>{item.name}</h3>
                            <p className="cart-item-price">
                              kr {unbundledUnitPrice.toFixed(2)}
                              {item.discounted_price && item.discounted_price < item.price && (
                                <span className="original-price"> (was kr {item.price.toFixed(2)})</span>
                              )}
                            </p>
                            {item.weight && item.weight > 0 && (
                              <p className="cart-item-weight" style={{ fontSize: '0.85em', color: '#666' }}>📦 {item.weight}g</p>
                            )}
                          </div>
                          <div className="cart-item-quantity">
                            <label>Quantity:</label>
                            <div className="quantity-input quantity-input-readonly">
                              <span>{priced.unbundled_quantity}</span>
                            </div>
                          </div>
                          <div className="cart-item-subtotal">
                            <p>Subtotal: kr {(priced.unbundled_quantity * unbundledUnitPrice).toFixed(2)}</p>
                          </div>
                          <div className="cart-item-quantity-controls">
                            <label htmlFor={`qty-${item.id}`}>Change qty:</label>
                            <div className="quantity-input">
                              <button onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}>-</button>
                              <input
                                id={`qty-${item.id}`}
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                                onKeyDown={preventArrowKeyChange}
                              />
                              <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                            </div>
                          </div>
                        </div>,
                      ];
                    }

                    // Normal single row (no split)
                    const displayUnitPrice = priced?.adjusted_unit_price ?? (item.discounted_price && item.discounted_price < item.price ? item.discounted_price : item.price);
                    const displayLineTotal = priced?.line_total_after_offer ?? (displayUnitPrice * item.quantity);
                    const originalLineTotal = priced?.line_total_before_offer ?? (displayUnitPrice * item.quantity);
                    return [
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-details">
                      <h3>{item.name}</h3>
                      <p className="cart-item-price">
                        kr {displayUnitPrice.toFixed(2)}
                        {item.discounted_price && item.discounted_price < item.price && (
                          <span className="original-price"> (was kr {item.price.toFixed(2)})</span>
                        )}
                      </p>
                      {item.weight && item.weight > 0 && (
                        <p className="cart-item-weight" style={{ fontSize: '0.85em', color: '#666' }}>📦 {item.weight}g</p>
                      )}
                    </div>
                    <div className="cart-item-quantity">
                      <label htmlFor={`qty-${item.id}`}>Quantity:</label>
                      <div className="quantity-input">
                        <button
                          onClick={() =>
                            updateQuantity(item.id, Math.max(1, item.quantity - 1))
                          }
                        >
                          -
                        </button>
                        <input
                          id={`qty-${item.id}`}
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            updateQuantity(item.id, parseInt(e.target.value) || 1)
                          }
                          onKeyDown={preventArrowKeyChange}
                        />
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="cart-item-subtotal">
                      <p>Subtotal: kr {displayLineTotal.toFixed(2)}</p>
                      {displayLineTotal < originalLineTotal && (
                        <p className="original-price" style={{ marginTop: '4px' }}>Before bundle offer: kr {originalLineTotal.toFixed(2)}</p>
                      )}
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="btn-remove"
                    >
                      Remove
                    </button>
                  </div>
                    ];
                  })}
              </div>
            </div>

            <aside className="cart-summary">
              <h2>Order Summary</h2>
              <div className="summary-content">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>kr {pricing.subtotalBeforeOffers.toFixed(2)}</span>
                </div>
                {pricing.offerDiscount > 0 && (
                  <div className="summary-row" style={{ color: '#166534' }}>
                    <span>Category offers:</span>
                    <span>- kr {pricing.offerDiscount.toFixed(2)}</span>
                  </div>
                )}
                {pricing.offerApplications?.length > 0 && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ fontWeight: 700, marginBottom: '8px', color: '#111827' }}>Applied offers:</div>
                    {pricing.offerApplications.map((offer) => (
                      <div key={`${offer.offer_id}-${offer.offer_type}`} style={{ fontSize: '0.9rem', marginBottom: '6px', color: '#374151' }}>
                        <span>{offer.title || (offer.offer_type === 'bundle' ? 'Bundle offer' : 'Offer')}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="tax-notice">
                  (including kr {getTotalVAT().toFixed(2)} in taxes)
                </p>
                
                {/* Shipping Information */}
                <div className="shipping-info">
                  <h3>Shipping Charges (Stockholm Area)</h3>
                  <div className="shipping-tiers">
                    <div className="shipping-tier">
                      <span>0-25 km:</span>
                      <span>kr 19</span>
                      <div className="shipping-tier-note">Free for orders above kr 100</div>
                    </div>
                    <div className="shipping-tier">
                      <span>25-40 km:</span>
                      <span>kr 29</span>
                      <div className="shipping-tier-note">Free for orders above kr 199</div>
                    </div>
                    <div className="shipping-tier">
                      <span>40-60 km:</span>
                      <span>kr 39</span>
                      <div className="shipping-tier-note">Free for orders above kr 299</div>
                    </div>
                    <div className="shipping-tier">
                      <span>Above 60 km:</span>
                      <span>kr 49</span>
                      <div className="shipping-tier-note">Free for orders above kr 449. Service point delivery.</div>
                    </div>
                  </div>
                  <p className="shipping-note">
                    *For orders outside the Stockholm area, minimum order value should be 599 kr for free delivery else 59 kr will be charged for shipping. Delivery will be made to the nearest service point.
                  </p>
                </div>

                <div className="summary-row total">
                  <span>Total:</span>
                  <span>kr {getTotalPrice().toFixed(2)}</span>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    if (hasUnavailableItems) {
                      setShowCheckoutValidation(true);
                    } else {
                      setShowCheckoutValidation(false);
                      navigate('/checkout');
                    }
                  }}
                >
                  Proceed to Checkout
                </button>
                <Link
                  to="/products"
                  className="btn btn-secondary"
                  style={{ width: '100%', textAlign: 'center' }}
                >
                  Continue Shopping
                </Link>
                <button
                  onClick={clearCart}
                  className="btn-clear-cart"
                >
                  Clear Cart
                </button>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;
